import { supabase } from './supabaseClient';
import { toHttpUrl } from './url';

// Link previews come from the `link-preview` Edge Function, because the
// browser cannot read a cross-origin page's HTML to find its og:image.
//
// Two-step on purpose. The function writes every result into `link_previews`,
// which the app can read directly — so a list of ten items costs one table
// query, not ten calls out to ten different shops. Only URLs missing from
// that table go to the function.

export async function listCachedPreviews(urls) {
  const keys = [...new Set(urls.map(toHttpUrl).filter(Boolean))];
  if (keys.length === 0) return {};

  const { data, error } = await supabase.from('link_previews').select('*').in('url', keys);
  if (error) throw error;

  return Object.fromEntries(data.map((row) => [row.url, row]));
}

// Asks the function to fetch (and cache) one URL. Returns the same shape as
// a cached row so callers don't branch on where it came from.
export async function fetchPreview(url) {
  const key = toHttpUrl(url);
  if (!key) return null;

  const { data, error } = await supabase.functions.invoke('link-preview', {
    body: { url: key },
  });
  if (error) throw error;

  return {
    url: key,
    image_url: data.imageUrl ?? null,
    title: data.title ?? null,
    site_name: data.siteName ?? null,
    status: data.status,
    error: data.error ?? null,
  };
}

// Fills in whatever isn't cached yet, a few at a time.
//
// Serialised in small batches rather than fired all at once: each miss is a
// real HTTP request to someone else's server, and a first load of twenty
// unseen links shouldn't open twenty sockets.
export async function resolvePreviews(urls, { onResolved, batchSize = 3 } = {}) {
  const cached = await listCachedPreviews(urls);
  if (onResolved) onResolved(cached);

  const misses = [...new Set(urls.map(toHttpUrl).filter(Boolean))].filter((u) => !cached[u]);

  const results = { ...cached };
  for (let i = 0; i < misses.length; i += batchSize) {
    const batch = misses.slice(i, i + batchSize);
    const settled = await Promise.allSettled(batch.map(fetchPreview));
    for (const outcome of settled) {
      if (outcome.status === 'fulfilled' && outcome.value) {
        results[outcome.value.url] = outcome.value;
      }
    }
    if (onResolved) onResolved({ ...results });
  }

  return results;
}
